import React from 'react';
import type { GuideSummary } from 'match';

type MatchProfileProps = {
    guide: GuideSummary;
    onBack: () => void;
    onHelp?: () => void;
    onViewSchedule: () => void;
};

const DEFAULT_CERTIFICATIONS = [
    'MAPS MDMA Therapy Training',
    'Psilocybin Facilitator Certification',
    'Licensed Clinical Psychologist'
];

const DEFAULT_SPECIALTIES = [
    'Trauma Processing',
    'Anxiety Management',
    'Depression Support',
    'Microdosing',
    'Marriage Counseling',
    'PTSD Treatment'
];

const DEFAULT_REASONS = [
    'An expert in PTSD management',
    'Located in your city',
    'Experienced in your religion'
];

export function MatchProfile({ guide, onBack, onHelp, onViewSchedule }: MatchProfileProps) {
    const certifications = guide.certifications?.length ? guide.certifications : DEFAULT_CERTIFICATIONS;
    const specialties = guide.specialties?.length ? guide.specialties : DEFAULT_SPECIALTIES;
    const reasons = guide.whyGreatForYou?.length ? guide.whyGreatForYou : DEFAULT_REASONS;

    return (
        <div className="min-h-screen bg-slate-100 px-4 py-12">
            <div className="mx-auto w-full max-w-sm overflow-hidden rounded-[32px] bg-white shadow-2xl shadow-slate-900/10">
                <header className="flex items-center justify-between px-5 pt-5">
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-lg text-slate-500"
                        aria-label="Go back"
                    >
                        ←
                    </button>
                    <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold uppercase text-white">
                            GC
                        </span>
                        <span className="text-base font-semibold text-slate-900">Guide Profile</span>
                    </div>
                    <button
                        type="button"
                        onClick={onHelp}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-500"
                        aria-label="Help"
                    >
                        ?
                    </button>
                </header>

                <main className="px-6 pb-10">
                    <div className="mt-6 rounded-[20px] bg-[#20388C] py-3 text-center text-[12px] font-semibold uppercase tracking-[0.32em] text-white">
                        - Guides Collective Certified -
                    </div>

                    <div className="mt-8 flex flex-col items-center text-center">
                        <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-slate-100 bg-indigo-50">
                            {guide.avatarUrl ? (
                                <img src={guide.avatarUrl} alt={guide.fullName ?? 'Guide portrait'} className="h-full w-full object-cover" />
                            ) : (
                                <span className="text-4xl text-indigo-300">🙂</span>
                            )}
                        </div>
                        <h2 className="mt-6 text-[24px] font-semibold text-slate-900">{guide.fullName ?? 'Your Guide'}</h2>
                        <p className="mt-1 text-sm text-slate-600">
                            {guide.title ?? 'Licensed Psychedelic Therapist'}
                            {guide.experienceYears ? ` • ${guide.experienceYears} years experience` : ''}
                        </p>
                    </div>

                    <section className="mt-9 space-y-3 text-left">
                        <h3 className="text-[16px] font-semibold text-slate-900">Background</h3>
                        <p className="text-sm leading-6 text-slate-600">
                            Dr. Mitchell is a licensed clinical psychologist specializing in psychedelic-assisted therapy. She holds a PhD in Clinical
                            Psychology from Stanford University and has completed specialized training in MDMA and psilocybin therapy protocols.
                        </p>
                    </section>

                    <section className="mt-8 text-left">
                        <h3 className="text-[16px] font-semibold text-slate-900">Certifications</h3>
                        <ul className="mt-4 space-y-3 text-sm text-slate-600">
                            {certifications.map(item => (
                                <li key={item} className="flex items-center gap-3">
                                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-50 text-indigo-500">★</span>
                                    <span className="font-medium">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="mt-8 text-left">
                        <h3 className="text-[16px] font-semibold text-slate-900">Top Specialties</h3>
                        <ul className="mt-4 flex flex-wrap gap-2">
                            {specialties.map(item => (
                                <li key={item} className="rounded-full bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600">
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="mt-8 text-left">
                        <h3 className="text-[16px] font-semibold text-slate-900">Session Types Offered</h3>
                        <div className="mt-4 space-y-4">
                            <article className="rounded-[18px] border border-slate-200 bg-slate-50/70 p-5">
                                <h4 className="text-sm font-semibold text-slate-900">Preparation Sessions</h4>
                                <p className="mt-1 text-sm text-slate-600">Individual preparation for psychedelic therapy</p>
                            </article>
                            <article className="rounded-[18px] border border-slate-200 bg-slate-50/70 p-5">
                                <h4 className="text-sm font-semibold text-slate-900">Guided Sessions</h4>
                                <p className="mt-1 text-sm text-slate-600">Supervised psychedelic therapy sessions</p>
                            </article>
                            <article className="rounded-[18px] border border-slate-200 bg-slate-50/70 p-5">
                                <h4 className="text-sm font-semibold text-slate-900">Integration Sessions</h4>
                                <p className="mt-1 text-sm text-slate-600">Post-session processing and integration</p>
                            </article>
                        </div>
                    </section>

                    <section className="mt-8 text-left">
                        <h3 className="text-[16px] font-semibold text-slate-900">Why Sarah Is Great for You</h3>
                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                            {reasons.map(reason => (
                                <li key={reason} className="flex items-start gap-3">
                                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                                        ★
                                    </span>
                                    <span>{reason}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <button
                        type="button"
                        onClick={onViewSchedule}
                        className="mt-9 inline-flex w-full items-center justify-center gap-2 rounded-[18px] bg-slate-900 py-4 text-sm font-semibold text-white shadow-lg shadow-slate-900/15"
                    >
                        <span className="text-base">📅</span>
                        Book Free Consultation
                    </button>
                </main>
            </div>
        </div>
    );
}
